import { Box, Feedback } from '@cubeyou-team/hx-react-components';
import React, { FC, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { QUERY_BUILDER_TYPES } from 'src/modules/query-builder/constants';
import { QueryToolbar } from 'src/modules/query-builder/components';
import {
  isTrialRestrictedFusionSource,
  useTrialPaywall,
} from 'src/modules/trial-paywall';
import { createOnDropObj, isEmpty } from 'src/utils/misc';

import {
  addConversationItemRequest,
  addQueryAnItemWithInfos,
  addQueryEntityRequest,
  changeGroupAtLeastNumberRequest,
  changeGroupOperatorRequest,
  deleteEntityRequest,
  moveConversationItemRequest,
  moveEntityRequest,
  resetConversationQueryError,
  selectGroupRequest,
  updateQueryEntityInfos,
} from '../../../../../actions';
import { mapDemographicItem } from '../../../../../mappers';
import { StyledDropBottom } from '../../../StyledQueryBuilder';
import QueryItem from '../../QueryItem';

interface QueryTargetProps {
  target: any;
  onItemDragToggle: (_value) => void;
  onItemDropped: () => void;
  onRequireMissingItemRequest: () => void;
  entityDragging: any;
  isQueryPreviewVisible: boolean;
  isStanleyQueryPanelVisible: boolean;
  isStanleyHistoryView: boolean;
  onStanleyQueryPanelToggle: () => void;
  onOpenStanleyHistory: () => void;
  isQueryLoading: boolean;
  onPreviewQueryRequest: () => void;
  onClearQueryRequest: () => void;
}
const QueryTarget: FC<QueryTargetProps> = ({
  target,
  entityDragging,
  isQueryPreviewVisible,
  isStanleyQueryPanelVisible,
  isStanleyHistoryView,
  onStanleyQueryPanelToggle,
  onOpenStanleyHistory,
  isQueryLoading,
  onItemDragToggle,
  onItemDropped,
  onRequireMissingItemRequest,
  onPreviewQueryRequest,
  onClearQueryRequest,
}) => {
  const dispatch = useDispatch();
  const queryBuilderType = useSelector(
    (state: any) => state.queryBuilder.queryBuilderType
  );
  const conversation = useSelector((state: any) => state.conversation);
  const { isTrial } = useTrialPaywall();

  const [dragOuterGroupEntered, setDragOuterGroupEntered] =
    useState<boolean>(false);

  const handleSetDragging = useCallback(
    (mode) => {
      if (mode) {
        setTimeout(() => onItemDragToggle(mode), 50);
        return;
      }

      onItemDragToggle(false);
    },
    [onItemDragToggle]
  );

  const handleOnDrop = useCallback(
    (
      targetId,
      {
        itemId,
        parentIdFrom,
        parentIdTo,
        entityIndex,
        itemInfos,
        isDemographic,
        demoClass,
      }
    ) => {
      setDragOuterGroupEntered(false);

      onItemDropped();

      if (parentIdFrom && entityIndex) {
        if (queryBuilderType === QUERY_BUILDER_TYPES.CONVERSATION) {
          return dispatch(
            moveConversationItemRequest(
              targetId,
              parentIdFrom,
              parseInt(entityIndex),
              itemId,
              parentIdTo
            )
          );
        }
        return dispatch(
          moveEntityRequest(
            targetId,
            parentIdFrom,
            parseInt(entityIndex),
            itemId,
            parentIdTo
          )
        );
      }

      if (!itemInfos) return;

      if (isTrial && isTrialRestrictedFusionSource(itemInfos.source)) return;

      if (isDemographic !== 'true') {
        if (queryBuilderType === QUERY_BUILDER_TYPES.CONVERSATION) {
          return dispatch(
            addConversationItemRequest(targetId, parentIdTo, itemInfos)
          );
        }
        return dispatch(
          addQueryAnItemWithInfos(targetId, parentIdTo, itemInfos)
        );
      }
      const demographicClass =
        demoClass === 'uk_country'
          ? 'country'
          : demoClass === 'uk_ethnicity'
          ? 'ethnicity'
          : demoClass;
      const demographicItem = mapDemographicItem(itemInfos, demographicClass);

      return dispatch(
        addQueryAnItemWithInfos(targetId, parentIdTo, demographicItem)
      );
    },
    [queryBuilderType, onItemDropped, dispatch, isTrial]
  );

  return (
    <div className="flex flex-col gap-4 px-6">
      {queryBuilderType === QUERY_BUILDER_TYPES.CONVERSATION &&
        (conversation.error || conversation.message) && (
          <Box pY="small" pX="small">
            <Feedback
              data-test-id="conversation-query-builder-feedback"
              status={conversation.error ? 'error' : 'success'}
              message={conversation.message}
              closable
              onFeedbackClose={() => dispatch(resetConversationQueryError())}
            />
          </Box>
        )}
      <QueryToolbar
        target={target}
        conversation={conversation}
        queryBuilderType={queryBuilderType}
        isQueryLoading={isQueryLoading}
        isQueryPreviewVisible={isQueryPreviewVisible}
        isStanleyQueryPanelVisible={isStanleyQueryPanelVisible}
        isStanleyHistoryView={isStanleyHistoryView}
        onStanleyQueryPanelToggle={onStanleyQueryPanelToggle}
        onOpenStanleyHistory={onOpenStanleyHistory}
        onPreviewQueryRequest={onPreviewQueryRequest}
        onClearQueryRequest={onClearQueryRequest}
      />
      <QueryItem
        item={target.target_query}
        parentGroupId={0}
        index={0}
        entitiesInfos={target.queryEntitiesInfos}
        conversationQueries={target.conversationQueries}
        isOuter
        entityDragging={entityDragging}
        onAddEntityRequest={(parentGroupId, entityType) => {
          dispatch(
            addQueryEntityRequest(target.target_id, entityType, parentGroupId)
          );
        }}
        onUpdateEntityRequest={(parentGroupId, infos, index) => {
          dispatch(
            updateQueryEntityInfos(
              target.target_id,
              parentGroupId,
              infos,
              index
            )
          );
        }}
        onDeleteEntityRequest={(parentGroupId, entityIndex) => {
          dispatch(
            deleteEntityRequest(target.target_id, parentGroupId, entityIndex)
          );
        }}
        onChangeGroupOperatorRequest={(groupId, operator) => {
          dispatch(
            changeGroupOperatorRequest(target.target_id, groupId, operator)
          );
        }}
        onEntityDragToggle={handleSetDragging}
        onItemDrop={(item) => handleOnDrop(target.target_id, item)}
        onRequireMissingItemRequest={onRequireMissingItemRequest}
        queryBuilderType={queryBuilderType}
        onItemDragStart={undefined}
        onSelectGroupRequest={(groupId) => {
          dispatch(selectGroupRequest(target.target_id, groupId));
        }}
        onChangeGroupAtLeastNumberRequest={(groupId, atLeast) => {
          dispatch(
            changeGroupAtLeastNumberRequest(target.target_id, groupId, atLeast)
          );
        }}
        onAddEntitiesListRequest={(groupId, items) => {
          items.forEach((item) => {
            dispatch(
              addConversationItemRequest(target.target_id, groupId, {
                query: item,
              })
            );
          });
        }}
      />
      <StyledDropBottom
        entityDraggingEnter={dragOuterGroupEntered}
        isEmpty={target?.target_query?.arguments_list?.length === 0}
        onDrop={(e) => handleOnDrop(target.target_id, createOnDropObj(e, 0))}
        onDragOver={(e) => e.preventDefault()}
        onDragEnter={(e) => {
          setDragOuterGroupEntered(true);
          e.stopPropagation();
          e.preventDefault();
        }}
        onDragLeave={() => setDragOuterGroupEntered(false)}
      />
    </div>
  );
};

export default QueryTarget;
